(() => {
  'use strict';

  if (customElements.get('pdp-v3')) return;

  /**
   * Parse a YouTube URL into an 11-char video id, or null.
   * Results are memoised — same string returns cached value.
   */
  const YT_ID_CACHE = new Map();
  const YT_HOSTS = new Set([
    'youtu.be',
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'youtube-nocookie.com',
    'www.youtube-nocookie.com',
  ]);

  function youtubeId(value) {
    if (!value) return null;
    if (YT_ID_CACHE.has(value)) return YT_ID_CACHE.get(value);

    let id = null;
    try {
      const url = new URL(value);
      if (url.protocol === 'https:') {
        if (url.hostname === 'youtu.be') {
          id = url.pathname.slice(1).split('/')[0];
        } else if (YT_HOSTS.has(url.hostname)) {
          id =
            url.searchParams.get('v') ||
            (/^\/(embed|shorts)\//.test(url.pathname) ? url.pathname.split('/')[2] : '') ||
            '';
        }
      }
    } catch {
      id = null;
    }

    const result = id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
    YT_ID_CACHE.set(value, result);
    return result;
  }

  class PDPV3 extends HTMLElement {
    #abort = null;
    #observer = null;

    connectedCallback() {
      if (this.#abort) return;
      this.#abort = new AbortController();

      this.#initVideoPlaceholders();
      this.#initComparisonSliders();
      this.#bindEvents();
      this.#initNavObserver();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
      this.#observer?.disconnect();
      this.#observer = null;
    }

    // -------------------------------------------------------------------------
    // Init helpers
    // -------------------------------------------------------------------------

    #initVideoPlaceholders() {
      for (const box of this.querySelectorAll('[data-p3-youtube]')) {
        const button = box.querySelector('[data-p3-play]');
        if (button && youtubeId(box.dataset.p3Youtube)) {
          button.hidden = false;
        }
      }
    }

    #initComparisonSliders() {
      for (const box of this.querySelectorAll('[data-p3-comparison]')) {
        const module = box.closest('.p3-module');
        const label = module?.querySelector('[data-p3-range-label]');
        const range = label?.querySelector('[data-p3-range]');
        if (!range) continue;

        box.classList.add('p3-ba--interactive');
        label.hidden = false;
        this.#updateComparison(range);
      }
    }

    #bindEvents() {
      this.addEventListener(
        'input',
        (event) => {
          if (event.target.matches('[data-p3-range]')) {
            this.#updateComparison(event.target);
          }
        },
        { signal: this.#abort.signal }
      );

      this.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('[data-p3-play]');
          if (!button || !this.contains(button)) return;

          const box = button.closest('[data-p3-youtube]');
          if (!box || box.dataset.loaded === 'true') return;

          const id = youtubeId(box.dataset.p3Youtube);
          if (!id) return;

          box.dataset.loaded = 'true';
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`;
          iframe.title = box.dataset.title || button.textContent.trim();
          iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.referrerPolicy = 'strict-origin-when-cross-origin';
          iframe.loading = 'lazy';

          box.replaceChildren(iframe);
          iframe.focus();
        },
        { signal: this.#abort.signal }
      );
    }

    #initNavObserver() {
      if (!('IntersectionObserver' in window)) return;

      const links = Array.from(this.querySelectorAll('.p3-nav a'));
      if (!links.length) return;

      const modules = Array.from(this.querySelectorAll('.p3-module'));
      if (!modules.length) return;

      const intersecting = new Set();

      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) intersecting.add(entry.target);
            else intersecting.delete(entry.target);
          }

          // Sort by current viewport top; never cache bounding boxes.
          const active = [...intersecting].sort(
            (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
          )[0];

          for (const link of links) {
            if (active && link.getAttribute('href') === '#' + active.id) {
              link.setAttribute('aria-current', 'location');
            } else {
              link.removeAttribute('aria-current');
            }
          }
        },
        { rootMargin: '0px 0px -35% 0px', threshold: 0 }
      );

      for (const module of modules) this.#observer.observe(module);
    }

    // -------------------------------------------------------------------------
    // Update helpers
    // -------------------------------------------------------------------------

    #updateComparison(range) {
      const value = Math.min(100, Math.max(0, Number(range.value) || 0));
      range.setAttribute('aria-valuetext', `${value}%`);
      range
        .closest('.p3-module')
        ?.querySelector('[data-p3-comparison]')
        ?.style.setProperty('--p3-position', `${value}%`);
    }
  }

  customElements.define('pdp-v3', PDPV3);
})();